import React, { useState, useEffect } from 'react';
import { Menu, X, Globe, ShoppingCart } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../context/LanguageContext';

const SHOP_URL = 'https://olion.odoo.com/shop';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { language, changeLanguage, t, direction } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    if (sectionId === 'catalogue') {
      window.location.href = '/catalogue';
    } else if (sectionId === 'technologie') {
      window.location.href = '/technologie';
    } else if (sectionId === 'industriel') {
      window.location.href = '/industriel';
    } else if (sectionId.startsWith('/')) {
      window.location.href = sectionId;
    } else if (window.location.pathname !== '/') {
      window.location.href = '/#' + sectionId;
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
        setIsMobileMenuOpen(false);
      }
    }
  };

  const languages = [
    { code: 'fr', label: 'FR', flag: '🇫🇷' },
    { code: 'en', label: 'EN', flag: '🇬🇧' },
    { code: 'ar', label: 'AR', flag: '🇹🇳' }
  ];

  const navItems = [
    { id: 'accueil', label: t('nav.home') },
    { id: 'produits', label: t('nav.products') },
    { id: 'catalogue', label: t('nav.catalog') },
    { id: 'shop', label: t('nav.shop'), external: SHOP_URL },
    { id: 'industriel', label: t('nav.industrial') },
    { id: 'technologie', label: t('nav.about') },
    { id: 'partenaires', label: t('nav.partners') },
    { id: 'localisation', label: t('nav.locations') },
    { id: 'contact', label: t('nav.contact') }
  ];

  return (
    <header
      className={`fixed top-0 ${direction === 'rtl' ? 'right-0 left-0' : 'left-0 right-0'} z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-lg'
          : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`flex items-center justify-between h-20 ${direction === 'rtl' ? 'flex-row-reverse' : ''}`}>
          {/* Logo */}
          <div className="flex items-center cursor-pointer" onClick={() => scrollToSection('accueil')}>
            <img
              src="https://customer-assets.emergentagent.com/job_5584cb6f-917f-481f-bd8e-c6188ed999cc/artifacts/fyqtzyw4_Screenshot_20260214_211850_WhatsApp.jpg"
              alt="Olion Logo"
              className="h-14 w-auto transition-transform duration-300 hover:scale-105"
            />
          </div>

          {/* Desktop Navigation */}
          <nav className={`hidden md:flex items-center ${direction === 'rtl' ? 'space-x-reverse space-x-1' : 'space-x-1'}`}>
            {navItems.map((item) => (
              item.external ? (
                <a
                  key={item.id}
                  href={item.external}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`nav-${item.id}-link`}
                  className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
                    isScrolled
                      ? 'text-blue-800 hover:text-blue-600 hover:bg-blue-50'
                      : 'text-blue-900 hover:text-blue-700 hover:bg-blue-50/30'
                  }`}
                >
                  {item.label}
                </a>
              ) : (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
                    isScrolled
                      ? 'text-blue-800 hover:text-blue-600 hover:bg-blue-50'
                      : 'text-blue-900 hover:text-blue-700 hover:bg-blue-50/30'
                  }`}
                >
                  {item.label}
                </button>
              )
            ))}
          </nav>

          {/* Language Switcher & CTA */}
          <div className={`hidden md:flex items-center ${direction === 'rtl' ? 'space-x-reverse space-x-4' : 'space-x-4'} ml-auto`}>
            <div className={`flex items-center ${direction === 'rtl' ? 'space-x-reverse space-x-2' : 'space-x-2'} bg-blue-50/80 backdrop-blur-sm rounded-full p-1 border border-blue-200`}>
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => changeLanguage(lang.code)}
                  className={`px-3 py-1 rounded-full text-sm font-semibold transition-all duration-300 ${
                    language === lang.code
                      ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white'
                      : 'text-blue-700 hover:bg-blue-100'
                  }`}
                >
                  {lang.flag} {lang.label}
                </button>
              ))}
            </div>
            
            <Button
              onClick={() => scrollToSection('contact')}
              className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-6 py-2 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
            >
              {t('nav.contact')}
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 rounded-lg transition-colors duration-300"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? (
              <X className={`w-6 h-6 ${isScrolled ? 'text-gray-700' : 'text-white'}`} />
            ) : (
              <Menu className={`w-6 h-6 ${isScrolled ? 'text-gray-700' : 'text-white'}`} />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden py-4 bg-white/95 backdrop-blur-md rounded-lg mt-2 shadow-xl">
            <nav className="flex flex-col space-y-2 px-4">
              {navItems.map((item) => (
                item.external ? (
                  <a
                    key={item.id}
                    href={item.external}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`nav-mobile-${item.id}-link`}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`${direction === 'rtl' ? 'text-right' : 'text-left'} px-4 py-3 rounded-lg font-medium text-gray-700 hover:text-blue-600 hover:bg-blue-50 transition-all duration-300`}
                  >
                    {item.label}
                  </a>
                ) : (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className={`${direction === 'rtl' ? 'text-right' : 'text-left'} px-4 py-3 rounded-lg font-medium text-gray-700 hover:text-blue-600 hover:bg-blue-50 transition-all duration-300`}
                  >
                    {item.label}
                  </button>
                )
              ))}
              
              {/* Mobile Language Switcher */}
              <div className={`flex ${direction === 'rtl' ? 'flex-row-reverse justify-end' : ''} gap-2 px-4 py-2`}>
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => changeLanguage(lang.code)}
                    className={`flex-1 px-3 py-2 rounded-lg text-sm font-semibold transition-all duration-300 ${
                      language === lang.code
                        ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {lang.flag} {lang.label}
                  </button>
                ))}
              </div>
              
              <Button
                onClick={() => scrollToSection('contact')}
                className="bg-gradient-to-r from-orange-500 to-orange-600 text-white w-full py-3 rounded-lg font-semibold mt-2"
              >
                {t('nav.contact')}
              </Button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};
